import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import { adminApiRequest } from "../utils/adminApi";
import { useToast } from "../utils/ToastContext";

const EditItemModal = ({
  showEditModal,
  handleCloseEditModal,
  handleEditSubmit,
  editingItem,
  setEditingItem,
  categories,
  editFormHandlePhotoChange,
  editFormHandleRemovePhoto,
  placeholderImage,
  setRemovedOptions,
  updatedRequiredOptions,
  setUpdatedRequiredOptions,
  updatedOptionalOptions,
  setUpdatedOptionalOptions,
  isSubmitting,
}) => {
  const { showToast } = useToast();
  const [existingRequiredOptions, setExistingRequiredOptions] = useState([]);
  const [existingOptionalOptions, setExistingOptionalOptions] = useState([]);
  const [selectedUpdatedRequiredOption, setselectedUpdatedRequiredOption] =
    useState("");
  const [selectedUpdatedOptionalOption, setselectedUpdatedOptionalOption] =
    useState("");
  const [togglingOptionId, setTogglingOptionId] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadOptions = async () => {
      if (!showEditModal) return;

      setselectedUpdatedRequiredOption("");
      setselectedUpdatedOptionalOption("");

      try {
        const res = await adminApiRequest({ script: "fetch-options" });

        if (!res?.success) {
          throw new Error(res?.error || "Failed to fetch options");
        }

        if (!isMounted) return;

        setExistingRequiredOptions(res.data?.required_options || []);
        setExistingOptionalOptions(res.data?.optional_options || []);
      } catch (err) {
        if (!isMounted) return;
        console.error("fetch-options error:", err?.message);
        setExistingRequiredOptions([]);
        setExistingOptionalOptions([]);
      }
    };

    loadOptions();

    return () => {
      isMounted = false;
    };
  }, [showEditModal]);

  useEffect(() => {
    let isMounted = true;

    const loadItemOptions = async () => {
      if (!editingItem?.id) return;

      try {
        const res = await adminApiRequest({
          script: "fetch-item-options",
          data: { item_id: editingItem.id },
        });

        if (!res?.success) {
          throw new Error(res?.error || "Failed to fetch item options");
        }

        if (!isMounted) return;

        setUpdatedRequiredOptions(
          (res.data?.required_options || []).map((option) => ({
            ...option,
            id: option?.id ? Number(option.id) : null,
            isEditable: true,
          })),
        );

        setUpdatedOptionalOptions(
          (res.data?.optional_options || []).map((option) => ({
            ...option,
            id: option?.id ? Number(option.id) : null,
            isEditable: true,
          })),
        );
      } catch (err) {
        if (!isMounted) return;
        console.error("fetch-item-options error:", err?.message);
        setUpdatedRequiredOptions([]);
        setUpdatedOptionalOptions([]);
      }
    };

    loadItemOptions();

    return () => {
      isMounted = false;
    };
  }, [editingItem?.id, setUpdatedOptionalOptions, setUpdatedRequiredOptions]);

  const handleRemoveOption = (type, index) => {
    if (isSubmitting) return;

    if (type === "required") {
      const optionToRemove = updatedRequiredOptions[index];

      if (optionToRemove?.id) {
        setRemovedOptions((prev) => [...prev, Number(optionToRemove.id)]);
      }

      setUpdatedRequiredOptions((prev) => prev.filter((_, i) => i !== index));
    } else {
      const optionToRemove = updatedOptionalOptions[index];

      if (optionToRemove?.id) {
        setRemovedOptions((prev) => [...prev, Number(optionToRemove.id)]);
      }

      setUpdatedOptionalOptions((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleAddOption = (type) => {
    if (isSubmitting) return;

    const newOption = {
      id: null,
      option_name: "",
      option_price: "",
      isEditable: true,
      is_available: 1,
    };

    if (type === "required") {
      setUpdatedRequiredOptions((prev) => [...prev, newOption]);
    } else {
      setUpdatedOptionalOptions((prev) => [...prev, newOption]);
    }
  };

  const handleOptionChange = (type, index, field, value) => {
    if (type === "required") {
      setUpdatedRequiredOptions((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        return updated;
      });
    } else {
      setUpdatedOptionalOptions((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        return updated;
      });
    }
  };

  const handleToggleOptionAvailability = async (type, index, option) => {
    if (isSubmitting) return;
    const currentStatus = option.is_available !== undefined ? Number(option.is_available) : 1;
    const newStatus = currentStatus === 1 ? 0 : 1;
    if (option.id) {
      setTogglingOptionId(`${type}-${index}`);
      try {
        const res = await adminApiRequest({
          script: "toggle-option-status",
          data: { id: option.id, is_available: newStatus },
        });
        if (res?.success) {
          handleOptionChange(type, index, "is_available", newStatus);
        } else {
          showToast(res?.error || "Failed to update option availability");
        }
      } catch (err) {
        showToast(err?.message || "Error updating option availability");
      } finally {
        setTogglingOptionId(null);
      }
    } else {
      handleOptionChange(type, index, "is_available", newStatus);
    }
  };

  const handleSelectExistingOption = (type, optionIdRaw) => {
    if (!optionIdRaw || isSubmitting) return;

    const optionId = Number(optionIdRaw);

    const optionsPool =
      type === "required" ? existingRequiredOptions : existingOptionalOptions;

    const selectedOption = optionsPool.find(
      (opt) => Number(opt.id) === optionId,
    );
    if (!selectedOption) return;

    const currentOptions =
      type === "required" ? updatedRequiredOptions : updatedOptionalOptions;

    if (currentOptions.some((opt) => Number(opt.id) === optionId)) return;

    const formattedOption = {
      ...selectedOption,
      id: Number(selectedOption.id),
      isEditable: false,
    };

    if (type === "required") {
      setUpdatedRequiredOptions((prev) => [...prev, formattedOption]);
    } else {
      setUpdatedOptionalOptions((prev) => [...prev, formattedOption]);
    }
  };

  const getEditImageSrc = () => {
    if (!editingItem.image) return placeholderImage;

    if (
      typeof editingItem.image === "string" &&
      editingItem.image.startsWith("data:")
    ) {
      return editingItem.image;
    }

    if (typeof editingItem.image === "string") {
      return `https://lasgidirestaurant.com/${editingItem.image}`;
    }

    return placeholderImage;
  };

  const inputClass =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-2 focus:ring-slate-200";

  const disabledInputClass =
    "w-full rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3 text-sm text-slate-500 outline-none cursor-not-allowed";

  const sectionTitleClass = "text-sm font-semibold text-slate-900";
  const helperTextClass = "text-xs text-slate-500";

  const getInputClass = (disabled = false) =>
    disabled ? disabledInputClass : inputClass;

  return (
    showEditModal && (
      <Modal
        title="Edit Menu Item"
        onClose={isSubmitting ? undefined : handleCloseEditModal}
        onSubmit={handleEditSubmit}
        submitButtonText={isSubmitting ? "Saving..." : "Update Item"}
        showCancel={false}
        showSubmitButton={true}
        className="modal-content"
        disableSubmit={isSubmitting}
      >
        <div className="max-h-[60dvh] overflow-y-auto pr-1">
          <div className="space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className={sectionTitleClass}>Photo</p>

              <div className="mt-4">
                <img
                  src={getEditImageSrc()}
                  alt="Menu Item"
                  className="mx-auto h-40 w-full max-w-xs rounded-2xl border border-slate-200 bg-white object-cover"
                />
              </div>

              <input
                type="file"
                accept="image/jpeg, image/jpg, image/png, image/webp"
                onChange={editFormHandlePhotoChange}
                id="fileInput"
                className="hidden"
                disabled={isSubmitting}
              />

              <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                <label
                  htmlFor={isSubmitting ? undefined : "fileInput"}
                  className={`inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
                    isSubmitting
                      ? "cursor-not-allowed bg-slate-300 text-white"
                      : "cursor-pointer bg-slate-900 text-white hover:bg-slate-800"
                  }`}
                >
                  Choose File
                </label>

                {editingItem.image && (
                  <button
                    type="button"
                    onClick={editFormHandleRemovePhoto}
                    disabled={isSubmitting}
                    className={`inline-flex items-center justify-center rounded-xl border px-4 py-2.5 text-sm font-semibold transition ${
                      isSubmitting
                        ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                        : "border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                    }`}
                  >
                    Remove
                  </button>
                )}
              </div>

              <p className="mt-3 break-all text-center text-xs text-slate-500">
                {editingItem?.file
                  ? editingItem.file.name
                  : typeof editingItem.image === "string" &&
                      !editingItem.image.startsWith("data:")
                    ? editingItem.image.split("/").pop()
                    : "No file chosen"}
              </p>
            </div>

            <div className="space-y-2">
              <label className={sectionTitleClass}>Item Name</label>
              <input
                type="text"
                value={editingItem.item_name}
                onChange={(e) =>
                  setEditingItem({ ...editingItem, item_name: e.target.value })
                }
                className={getInputClass(isSubmitting)}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <label className={sectionTitleClass}>Item Description</label>
              <textarea
                value={editingItem.item_description}
                rows={4}
                onChange={(e) =>
                  setEditingItem({
                    ...editingItem,
                    item_description: e.target.value,
                  })
                }
                className={`${getInputClass(isSubmitting)} resize-none`}
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <label className={sectionTitleClass}>Price (£)</label>
              <input
                type="number"
                step="0.1"
                value={editingItem.price}
                onChange={(e) =>
                  setEditingItem({ ...editingItem, price: e.target.value })
                }
                className={getInputClass(isSubmitting)}
                disabled={isSubmitting}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className={sectionTitleClass}>Category</label>
                <select
                  value={editingItem.category_id}
                  onChange={(e) => {
                    const selectedCategory = e.target.value;
                    setEditingItem({
                      ...editingItem,
                      category_id: selectedCategory,
                      subcategory_id: "",
                    });
                  }}
                  className={getInputClass(isSubmitting)}
                  required
                  disabled={isSubmitting}
                >
                  <option value="">Select Category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.category_name}
                    </option>
                  ))}
                </select>
              </div>

              {editingItem.category_id &&
                categories.find(
                  (cat) => String(cat.id) === String(editingItem.category_id),
                )?.subcategories.length > 0 && (
                  <div className="space-y-2">
                    <label className={sectionTitleClass}>Sub-category</label>
                    <select
                      value={editingItem.subcategory_id || ""}
                      onChange={(e) =>
                        setEditingItem({
                          ...editingItem,
                          subcategory_id: e.target.value,
                        })
                      }
                      className={getInputClass(isSubmitting)}
                      disabled={isSubmitting}
                    >
                      <option value="">None</option>
                      {categories
                        .find(
                          (cat) =>
                            String(cat.id) === String(editingItem.category_id),
                        )
                        ?.subcategories.map((sub) => (
                          <option key={sub.id} value={sub.id}>
                            {sub.category_name}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="space-y-2">
                <p className={sectionTitleClass}>
                  Required Options (Radio Buttons)
                </p>
                <p className={helperTextClass}>
                  Select from existing options or add a new one below.
                </p>
              </div>

              <div className="mt-4">
                <select
                  value={selectedUpdatedRequiredOption}
                  onChange={(e) => {
                    setselectedUpdatedRequiredOption(e.target.value);
                    handleSelectExistingOption("required", e.target.value);
                  }}
                  className={getInputClass(isSubmitting)}
                  disabled={isSubmitting}
                >
                  <option value="">Select an option</option>
                  {existingRequiredOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.option_name} (£
                      {Number(option.option_price || 0).toFixed(2)})
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-4 space-y-3">
                {updatedRequiredOptions.map((option, index) => (
                  <div
                    key={`${option.id ?? "new"}-${index}`}
                    className="rounded-2xl border border-slate-200 bg-white p-3"
                  >
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-[2fr_1fr_auto_auto] items-center">
                      <input
                        type="text"
                        value={option.option_name}
                        readOnly={!option.isEditable || isSubmitting}
                        disabled={!option.isEditable || isSubmitting}
                        placeholder="Option name"
                        onChange={(e) =>
                          handleOptionChange(
                            "required",
                            index,
                            "option_name",
                            e.target.value,
                          )
                        }
                        className={getInputClass(
                          !option.isEditable || isSubmitting,
                        )}
                      />

                      <input
                        type="text"
                        inputMode="decimal"
                        value={option.option_price}
                        readOnly={!option.isEditable || isSubmitting}
                        disabled={!option.isEditable || isSubmitting}
                        placeholder="Price"
                        onChange={(e) =>
                          handleOptionChange(
                            "required",
                            index,
                            "option_price",
                            e.target.value,
                          )
                        }
                        onBlur={(e) => {
                          const val = parseFloat(e.target.value);
                          if (!isNaN(val)) {
                            handleOptionChange("required", index, "option_price", val.toFixed(2));
                          }
                        }}
                        className={getInputClass(
                          !option.isEditable || isSubmitting,
                        )}
                      />

                      <div className="flex items-center justify-center px-1">
                        <label
                          className={`inline-flex flex-col items-center justify-center cursor-pointer select-none ${
                            isSubmitting || togglingOptionId === `required-${index}`
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          title={Number(option.is_available) === 0 || option.is_available === false ? "Mark Available" : "Mark Sold Out"}
                        >
                          <input
                            type="checkbox"
                            checked={Number(option.is_available) !== 0 && option.is_available !== false}
                            onChange={() => handleToggleOptionAvailability("required", index, option)}
                            disabled={isSubmitting || togglingOptionId === `required-${index}`}
                            className="sr-only peer"
                          />
                          <div className="relative w-11 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                          <span className={`mt-1 text-[11px] font-bold whitespace-nowrap ${Number(option.is_available) === 0 || option.is_available === false ? "text-rose-600" : "text-emerald-600"}`}>
                            {Number(option.is_available) === 0 || option.is_available === false ? "Sold Out" : "In Stock"}
                          </span>
                        </label>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemoveOption("required", index)}
                        disabled={isSubmitting}
                        title="Delete Option"
                        className={`inline-flex items-center justify-center rounded-xl border p-3 transition ${
                          isSubmitting
                            ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                            : "border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                        }`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={() => handleAddOption("required")}
                disabled={isSubmitting}
                className={`mt-4 inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
                  isSubmitting
                    ? "cursor-not-allowed bg-slate-300 text-white"
                    : "bg-slate-900 text-white hover:bg-slate-800"
                }`}
              >
                Add Option
              </button>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="space-y-2">
                <p className={sectionTitleClass}>
                  Optional Options (Checkboxes)
                </p>
                <p className={helperTextClass}>
                  Select from existing options or add a new one below.
                </p>
              </div>

              <div className="mt-4">
                <select
                  value={selectedUpdatedOptionalOption}
                  onChange={(e) => {
                    setselectedUpdatedOptionalOption(e.target.value);
                    handleSelectExistingOption("optional", e.target.value);
                  }}
                  className={getInputClass(isSubmitting)}
                  disabled={isSubmitting}
                >
                  <option value="">Select an option</option>
                  {existingOptionalOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.option_name} (£
                      {Number(option.option_price || 0).toFixed(2)})
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-4 space-y-3">
                {updatedOptionalOptions.map((option, index) => (
                  <div
                    key={`${option.id ?? "new"}-${index}`}
                    className="rounded-2xl border border-slate-200 bg-white p-3"
                  >
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-[2fr_1fr_auto_auto] items-center">
                      <input
                        type="text"
                        value={option.option_name}
                        readOnly={!option.isEditable || isSubmitting}
                        disabled={!option.isEditable || isSubmitting}
                        placeholder="Option name"
                        onChange={(e) =>
                          handleOptionChange(
                            "optional",
                            index,
                            "option_name",
                            e.target.value,
                          )
                        }
                        className={getInputClass(
                          !option.isEditable || isSubmitting,
                        )}
                      />

                      <input
                        type="text"
                        inputMode="decimal"
                        value={option.option_price}
                        readOnly={!option.isEditable || isSubmitting}
                        disabled={!option.isEditable || isSubmitting}
                        placeholder="Price"
                        onChange={(e) =>
                          handleOptionChange(
                            "optional",
                            index,
                            "option_price",
                            e.target.value,
                          )
                        }
                        onBlur={(e) => {
                          const val = parseFloat(e.target.value);
                          if (!isNaN(val)) {
                            handleOptionChange("optional", index, "option_price", val.toFixed(2));
                          }
                        }}
                        className={getInputClass(
                          !option.isEditable || isSubmitting,
                        )}
                      />

                      <div className="flex items-center justify-center px-1">
                        <label
                          className={`inline-flex flex-col items-center justify-center cursor-pointer select-none ${
                            isSubmitting || togglingOptionId === `optional-${index}`
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          title={Number(option.is_available) === 0 || option.is_available === false ? "Mark Available" : "Mark Sold Out"}
                        >
                          <input
                            type="checkbox"
                            checked={Number(option.is_available) !== 0 && option.is_available !== false}
                            onChange={() => handleToggleOptionAvailability("optional", index, option)}
                            disabled={isSubmitting || togglingOptionId === `optional-${index}`}
                            className="sr-only peer"
                          />
                          <div className="relative w-11 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                          <span className={`mt-1 text-[11px] font-bold whitespace-nowrap ${Number(option.is_available) === 0 || option.is_available === false ? "text-rose-600" : "text-emerald-600"}`}>
                            {Number(option.is_available) === 0 || option.is_available === false ? "Sold Out" : "In Stock"}
                          </span>
                        </label>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemoveOption("optional", index)}
                        disabled={isSubmitting}
                        title="Delete Option"
                        className={`inline-flex items-center justify-center rounded-xl border p-3 transition ${
                          isSubmitting
                            ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                            : "border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                        }`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={() => handleAddOption("optional")}
                disabled={isSubmitting}
                className={`mt-4 inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
                  isSubmitting
                    ? "cursor-not-allowed bg-slate-300 text-white"
                    : "bg-slate-900 text-white hover:bg-slate-800"
                }`}
              >
                Add Option
              </button>
            </div>
          </div>
        </div>
      </Modal>
    )
  );
};

export default EditItemModal;